<template>
  <div>
    <div
      v-if="!isLoading"
      ref="assessmentsContainer"
      class="assessments-container"
      :class="{
        'justify-start': !isEmpty || desiredTestDate,
        'justify-center items-center': isEmpty && !desiredTestDate,
      }"
    >
      <div
        class="px-4 pt-5 -mb-10"
        :class="
          hasNoCodilityTest && desiredTestDate
            ? 'flex justify-between w-full'
            : 'inline-flex justify-end'
        "
      >
        <div
          v-if="hasNoCodilityTest && desiredTestDate"
          class="text-lg mt-2"
        >
          Pending tests
        </div>
        <BaseButton
          v-if="!isEmpty && !(hasNoCodilityTest && desiredTestDate)"
          class="icon-primary py-2.5 text-body-sm"
          outlined
          rounded
          icon-prepend="plus"
          icon-size="20px"
          @click="handleOpenGenerateDialog"
        >
          New test
        </BaseButton>
      </div>

      <div
        v-if="hasNoCodilityTest && desiredTestDate"
        class="flex w-full"
      >
        <AppCard
          class="mx-4 relative"
          width="19rem"
          height="12rem"
        >
          <AppCardTitle title="Test pending" />
          <div class="flex justify-between mt-5">
            <div class="font-semibold text-base">Desired date</div>
            <div
              class="pl-1 text-base"
              :class="desiredDateClass"
            >
              {{ desiredDate }}
            </div>
          </div>
          <div
            class="absolute text-proxify-primary cursor-pointer inline-block bottom-4 left-4"
            @click="handleOpenGenerateDialog"
          >
            Generate test
          </div>
        </AppCard>
      </div>
      <div
        v-if="isEmpty && !desiredTestDate"
        class="text-center w-[352px]"
      >
        <div class="font-semibold text-body-md">No tests generated yet</div>
        <div class="font-normal text-body-sm text-proxify-gray-600">
          When someone generate a test, it will appear here
        </div>
        <BaseButton
          class="icon-white text-white !text-body-md bg-proxify-primary-600 py-2 mt-4"
          rounded
          icon-prepend="plus"
          icon-size="20px"
          @click="handleOpenGenerateDialog"
        >
          Generate a test
        </BaseButton>
      </div>
      <div v-if="!isEmpty">
        <div
          v-if="unfinishedTests.length"
          class="assessments-title"
        >
          <span>In progress</span>
          <BaseBadge
            color="primary"
            class="!py-0 !px-2.5 !h-[21px] !w-[27px]"
          >
            {{ unfinishedTests.length }}
          </BaseBadge>
        </div>

        <div class="grid xl:grid-cols-2 gap-4 mx-4">
          <div
            v-for="assessment in [...unfinishedTests]"
            :key="assessment.id"
            :ref="unfinishedTestRefs.set"
          >
            <ApplicantAssessmentCard
              :assessment="assessment"
              :is-completed="false"
              :is-combined-technical-challenge="isCombinedTechnicalChallenge"
              :is-refreshing="isRefreshing"
              :hovered-task="hoveredTask"
              @hover="onHover"
              @copy="copy"
              @refresh="handleRefreshResult"
              @download="handleDownloadResult"
            />
          </div>
        </div>
        <div
          v-if="completedTests.length"
          class="assessments-title"
        >
          <span>Completed</span>
          <BaseBadge
            color="primary"
            class="!py-0 !px-2.5 !h-[21px] !w-[27px]"
          >
            {{ completedTests.length }}
          </BaseBadge>
        </div>

        <div class="grid xl:grid-cols-2 gap-4 mx-4">
          <div
            v-for="assessment in [...completedTests]"
            :key="assessment.id"
            :ref="completedTestRefs.set"
          >
            <ApplicantAssessmentCard
              :assessment="assessment"
              :is-completed="true"
              :is-refreshing="isRefreshing"
              :hovered-task="hoveredTask"
              @hover="onHover"
              @copy="copy"
              @refresh="handleRefreshResult"
              @download="handleDownloadResult"
            />
          </div>
        </div>
      </div>
    </div>
    <PDialog
      v-model="codilityActionsPopup"
      persistent
      position="standard"
    >
      <ApplicantGenerateTestDialog
        :on-generate-test="fetchAllTests"
        :application-id="$route.params.id"
        :on-close="() => (codilityActionsPopup = false)"
        :current-stage="currentStage"
      />
    </PDialog>
  </div>
</template>

<script>
import { defineAsyncComponent, ref, toRefs, watch } from 'vue';
import { computedEager, useClipboard, useTemplateRefsList } from '@vueuse/core';
import { vElementHover } from '@vueuse/components';
import { useStore } from 'vuex';
import ApplicantGenerateTestDialog from '@/components/Elements/Applicant/ApplicantGenerateTestDialog.vue';
import ApplicantAssessmentCard from '@/components/Elements/Applicant/ApplicantAssessmentCard.vue';
import api from '@/api';
import moment from 'moment';
import PDialog from '@/components/ProxifyUI/PDialog.vue';
import BaseBadge from '@/components/Base/BaseBadge.vue';
import BaseButton from '@/components/Base/BaseButton.vue';
import englishAssessmentUtils from '@/utils/englishAssessmentUtils';

export default {
  name: 'ApplicantActivityTests',
  components: {
    ApplicantGenerateTestDialog,
    ApplicantAssessmentCard,
    PDialog,
    BaseBadge,
    BaseButton,
    AppCard: defineAsyncComponent(() => import('@/components/App/AppCard.vue')),
    AppCardTitle: defineAsyncComponent(
      () => import('@/components/App/AppCardTitle.vue')
    ),
  },
  directives: {
    elementHover: vElementHover,
  },
  props: {
    codilityTests: {
      type: Array,
      required: true,
    },
    englishTests: {
      type: Array,
      required: true,
    },
    isCombinedTechnicalChallenge: {
      type: Boolean,
      default: false,
    },
    applicantName: {
      type: String,
      required: false,
      default() {
        return '';
      },
    },
    desiredTestDate: {
      type: String,
      default() {
        return '';
      },
    },
    fetchCodilityTests: {
      type: Function,
      required: true,
    },
    fetchEnglishTests: {
      type: Function,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: true,
    },
    currentStage: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  setup(props) {
    const store = useStore();
    const { codilityTests, englishTests, applicantName } = toRefs(props);
    const newTestDialog = ref(null);
    const completedTestReferences = useTemplateRefsList();
    const assessmentsContainer = ref(null);
    const { copy, copied } = useClipboard();
    const unfinishedTestReferences = useTemplateRefsList();
    const codilityActionsPopup = ref(false);

    const isRefreshing = ref(false);
    const hoveredTask = ref(null);
    const onHover = (id, taskName, state) => {
      hoveredTask.value = state ? id + taskName : null;
    };
    watch(
      () => copied.value,
      (isCopied) => {
        if (isCopied) {
          store.commit('ui/addSnackbarMessage', {
            title: 'Link copied to clipboard!',
            type: 'success',
          });
        }
      }
    );

    const tests = computedEager(() => {
      return [...englishTests.value, ...codilityTests.value];
    });
    const isEmpty = computedEager(() => !tests.value?.length);
    const hasNoCodilityTest = computedEager(() => !codilityTests.value?.length);

    const completedTests = computedEager(
      () =>
        tests.value?.filter(
          (test) =>
            Boolean(test.result?.close_date) || test.status === 'completed'
        ) ?? []
    );
    const unfinishedTests = computedEager(
      () =>
        tests.value?.filter(
          (test) =>
            (test.type === 'codility'
              ? !test.result?.close_date
              : !test.finished_at) && test.status !== 'completed'
        ) ?? []
    );

    const handleOpenGenerateDialog = () => {
      codilityActionsPopup.value = true;
    };

    const handleRefreshResult = async (assessment) => {
      if (isRefreshing.value) return; // debounce double-clicks
      isRefreshing.value = true;
      try {
        const { id } = assessment;
        const { data } = await api.applications.tests.codility.getResult({
          codility_test_link_id: id,
        });
        if (data) {
          store.commit('ui/addSnackbarMessage', {
            title: 'Test result refreshed successfully!',
            type: 'success',
          });
          props.fetchCodilityTests();
        }
      } catch {
        store.commit('ui/addSnackbarMessage', {
          title: 'Unable to refresh test result. Please try again later.',
          type: 'error',
        });
      } finally {
        isRefreshing.value = false;
      }
    };

    const handleDownloadResult = async (assessment) => {
      const { id, test } = assessment;
      const { data } = await api.applications.tests.codility.downloadResult({
        codility_test_link_id: id,
      });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `Codility Test Result - ${test.name} (${applicantName.value}).pdf`
      );
      document.body.appendChild(link);
      link.click();
    };

    const getPercentageDataValue = (result, maxResult) => {
      return result != null ? (result / maxResult) * 100 : null;
    };

    const fetchAllTests = async () => {
      try {
        await props.fetchCodilityTests();
        await props.fetchEnglishTests();
      } catch (error) {
        console.error('Error fetching tests:', error);
      }
    };

    return {
      unfinishedTestRefs: unfinishedTestReferences,
      newTestDialog,
      completedTestRefs: completedTestReferences,
      assessmentsContainer,
      isEmpty,
      hasNoCodilityTest,
      isRefreshing,
      tests,
      completedTests,
      unfinishedTests,
      copy,
      onHover,
      hoveredTask,
      handleOpenGenerateDialog,
      handleDownloadResult,
      handleRefreshResult,
      getPercentageDataValue,
      codilityActionsPopup,
      englishAssessmentUtils,
      fetchAllTests,
    };
  },
  computed: {
    desiredDate() {
      return this.desiredTestDate
        ? moment(this.desiredTestDate).format('DD MMM YYYY')
        : '';
    },
    desiredDateClass() {
      return moment().isAfter(this.desiredTestDate, 'day')
        ? 'text-proxify-warning-500'
        : 'text-proxify-black';
    },
  },
};
</script>

<style scoped>
.assessments-container {
  @apply grow overflow-y-auto overflow-x-hidden flex flex-col h-full;
}

.assessments-title {
  @apply text-proxify-gray text-body-md mt-5 mb-4 ml-4 w-full flex items-center gap-2;
}
</style>
